import type { SubjectDraft, LabelDraft } from '@/contexts';

// ─── Public output types ───────────────────────────────────────────────────────

export interface AssetWarning {
  field: string;
  /** Storage path that needs to be uploaded to S3. */
  path: string;
  /** True = newly added asset. False = path has changed from previous value. */
  isAdded: boolean;
}

export interface SqlOutput {
  sql: string;
  assetWarnings: AssetWarning[];
}

// ─── Asset path helpers ────────────────────────────────────────────────────────

const ASSET_PATH_PREFIX = '/secure-assets/models/path/to/';
const baseName = (p: string): string => p.split('/').pop() ?? p;
const deriveThumbnailPath = (slug: string) => `/secure-assets/thumbnails/${slug}/${slug}-thumbnail.jpg`;

// ─── SQL value formatters ──────────────────────────────────────────────────────

/** Quote a string for SQL. Returns NULL for empty / null / undefined. */
const q = (val: string | null | undefined): string => {
  if (!val) return 'NULL';
  return `'${val.replace(/'/g, "''")}'`;
};

/** Format a number for SQL. Returns NULL for null / undefined. */
const n = (val: number | null | undefined): string => {
  if (val == null) return 'NULL';
  return String(val);
};

const b = (val: boolean): string => (val ? 'true' : 'false');

// ─── Variable name helpers ─────────────────────────────────────────────────────

const slugToVar = (slug: string) => slug.replace(/-/g, '_').replace(/[^a-z0-9_]/gi, '');
const labelVar  = (slug: string) => `v_label_${slugToVar(slug)}_id`;

// ─── Config3D field definitions ────────────────────────────────────────────────
// Each entry maps a DB column name to a getter on config3D and a formatter.

type Fmt = 'q' | 'n' | 'b';
type Config3D = SubjectDraft['config3D'];

interface FieldDef {
  col: string;
  get: (c: Config3D) => string | number | boolean | null | undefined;
  fmt: Fmt;
  /** SQL variable name for this field (asset path fields only). */
  varName?: string;
}

const fmtVal = (val: string | number | boolean | null | undefined, fmt: Fmt): string => {
  if (fmt === 'q') return q(val as string | null | undefined);
  if (fmt === 'b') return b(val as boolean);
  return n(val as number | null | undefined);
};

const CONFIG3D_FIELDS: FieldDef[] = [
  { col: 'model_path',                      get: c => c.modelPath,                          fmt: 'q', varName: 'v_model_path' },
  { col: 'texture_path',                    get: c => c.texturePath,                        fmt: 'q', varName: 'v_texture_path' },
  { col: 'context_model_path',              get: c => c.contextModelPath || null,            fmt: 'q', varName: 'v_context_model_path' },
  { col: 'reference_context_model_path',    get: c => c.referenceContextModelPath || null,   fmt: 'q', varName: 'v_ref_context_model_path' },
  { col: 'scale_x',                         get: c => c.scale[0],                                                    fmt: 'n' },
  { col: 'scale_y',                         get: c => c.scale[1],                                                    fmt: 'n' },
  { col: 'scale_z',                         get: c => c.scale[2],                                                    fmt: 'n' },
  { col: 'rotation_x',                      get: c => c.rotation[0],                                                 fmt: 'n' },
  { col: 'rotation_y',                      get: c => c.rotation[1],                                                 fmt: 'n' },
  { col: 'rotation_z',                      get: c => c.rotation[2],                                                 fmt: 'n' },
  { col: 'position_x',                      get: c => c.position[0],                                                 fmt: 'n' },
  { col: 'position_y',                      get: c => c.position[1],                                                 fmt: 'n' },
  { col: 'position_z',                      get: c => c.position[2],                                                 fmt: 'n' },
  { col: 'distance',                         get: c => c.dist,                                                        fmt: 'n' },
  { col: 'distance_min',                    get: c => c.distMin,                                                     fmt: 'n' },
  { col: 'distance_max',                    get: c => c.distMax,                                                     fmt: 'n' },
  { col: 'ambient_light',                   get: c => c.ambientLight,                                                fmt: 'n' },
  { col: 'axes',                             get: c => c.axes,                                                        fmt: 'b' },
  { col: 'axes_labels_x1',                  get: c => c.axesLabels.x1,                                               fmt: 'q' },
  { col: 'axes_labels_x2',                  get: c => c.axesLabels.x2,                                               fmt: 'q' },
  { col: 'axes_labels_y1',                  get: c => c.axesLabels.y1,                                               fmt: 'q' },
  { col: 'axes_labels_y2',                  get: c => c.axesLabels.y2,                                               fmt: 'q' },
  { col: 'axes_labels_z1',                  get: c => c.axesLabels.z1,                                               fmt: 'q' },
  { col: 'axes_labels_z2',                  get: c => c.axesLabels.z2,                                               fmt: 'q' },
  { col: 'label_visible_distance',          get: c => c.labelVisibleDist,                                            fmt: 'n' },
  { col: 'label_visible_range',             get: c => c.labelVisibleRange,                                           fmt: 'n' },
  { col: 'subject_context_position_x',      get: c => c.context.subjectContextEnabled ? c.context.subjectContext.position[0]   : null, fmt: 'n' },
  { col: 'subject_context_position_y',      get: c => c.context.subjectContextEnabled ? c.context.subjectContext.position[1]   : null, fmt: 'n' },
  { col: 'subject_context_position_z',      get: c => c.context.subjectContextEnabled ? c.context.subjectContext.position[2]   : null, fmt: 'n' },
  { col: 'subject_context_rotation_x',      get: c => c.context.subjectContextEnabled ? c.context.subjectContext.rotation[0]   : null, fmt: 'n' },
  { col: 'subject_context_rotation_y',      get: c => c.context.subjectContextEnabled ? c.context.subjectContext.rotation[1]   : null, fmt: 'n' },
  { col: 'subject_context_rotation_z',      get: c => c.context.subjectContextEnabled ? c.context.subjectContext.rotation[2]   : null, fmt: 'n' },
  { col: 'subject_context_scale_x',         get: c => c.context.subjectContextEnabled ? c.context.subjectContext.scale[0]      : null, fmt: 'n' },
  { col: 'subject_context_scale_y',         get: c => c.context.subjectContextEnabled ? c.context.subjectContext.scale[1]      : null, fmt: 'n' },
  { col: 'subject_context_scale_z',         get: c => c.context.subjectContextEnabled ? c.context.subjectContext.scale[2]      : null, fmt: 'n' },
  { col: 'reference_context_position_x',    get: c => c.context.referenceContext.position[0],                        fmt: 'n' },
  { col: 'reference_context_position_y',    get: c => c.context.referenceContext.position[1],                        fmt: 'n' },
  { col: 'reference_context_position_z',    get: c => c.context.referenceContext.position[2],                        fmt: 'n' },
  { col: 'reference_context_rotation_x',    get: c => c.context.referenceContext.rotation[0],                        fmt: 'n' },
  { col: 'reference_context_rotation_y',    get: c => c.context.referenceContext.rotation[1],                        fmt: 'n' },
  { col: 'reference_context_rotation_z',    get: c => c.context.referenceContext.rotation[2],                        fmt: 'n' },
  { col: 'reference_context_scale_x',       get: c => c.context.referenceContext.scale[0],                           fmt: 'n' },
  { col: 'reference_context_scale_y',       get: c => c.context.referenceContext.scale[1],                           fmt: 'n' },
  { col: 'reference_context_scale_z',       get: c => c.context.referenceContext.scale[2],                           fmt: 'n' },
  { col: 'full_context_rotation_x',         get: c => c.context.fullContext.rotation[0],                             fmt: 'n' },
  { col: 'full_context_rotation_y',         get: c => c.context.fullContext.rotation[1],                             fmt: 'n' },
  { col: 'full_context_rotation_z',         get: c => c.context.fullContext.rotation[2],                             fmt: 'n' },
];

// ─── INSERT builders (new subject) ────────────────────────────────────────────
// These use v_area_id / v_subject_id / labelVar() — no inline subqueries needed.

function insertSubject(draft: SubjectDraft): string {
  return [
    `INSERT INTO subjects (area_id, discipline_id, slug, name, description, type, thumbnail_path, created_by)`,
    `VALUES (`,
    `  v_area_id,`,
    `  ${n(draft.disciplineId)},`,
    `  ${q(draft.slug)},`,
    `  ${q(draft.title)},`,
    `  ${q(draft.description || null)},`,
    `  ${q(draft.type)},`,
    `  v_thumbnail_path,`,
    `  v_updated_by  -- replace with actual username`,
    `)`,
    `RETURNING id INTO v_subject_id;`,
  ].join('\n');
}

function insertConfig3D(draft: SubjectDraft): string {
  const cols = CONFIG3D_FIELDS.map((f) => `  ${f.col}`).join(',\n');
  const vals = CONFIG3D_FIELDS.map((f) => {
    const val = f.get(draft.config3D);
    if (f.varName) return `  ${val ? f.varName : 'NULL'}`;
    return `  ${fmtVal(val, f.fmt)}`;
  }).join(',\n');
  return [
    `INSERT INTO subject_3d_configs (`,
    `  subject_id,`,
    cols,
    `,  created_by`,
    `) VALUES (`,
    `  v_subject_id,`,
    vals,
    `,  v_updated_by`,
    `);`,
  ].join('\n');
}

function insertSubjectCategories(draft: SubjectDraft): string | null {
  if (draft.categoryIds.length === 0) return null;
  const rows = draft.categoryIds.map((c) => `  (v_subject_id, ${c.id}, v_updated_by)`).join(',\n');
  return `INSERT INTO subject_categories (subject_id, category_id, created_by) VALUES\n${rows};`;
}

function insertSubjectUnits(draft: SubjectDraft): string | null {
  if (draft.unitIds.length === 0) return null;
  const rows = draft.unitIds.map((id) => `  (v_subject_id, ${id}, v_updated_by)`).join(',\n');
  return `INSERT INTO subject_units (subject_id, unit_id, created_by) VALUES\n${rows};`;
}

function insertLabel(label: LabelDraft, subjectType: string): string[] {
  const lVar = labelVar(label.slug);
  const cfg = label.config3D;
  const blocks: string[] = [];

  blocks.push([
    `INSERT INTO labels (subject_id, slug, type, name, description, created_by)`,
    `VALUES (`,
    `  v_subject_id,`,
    `  ${q(label.slug)},`,
    `  ${q(subjectType)},`,
    `  ${q(label.title)},`,
    `  ${q(label.description || null)},`,
    `  v_updated_by`,
    `)`,
    `RETURNING id INTO ${lVar};`,
  ].join('\n'));

  blocks.push([
    `INSERT INTO label_3d_configs (`,
    `  label_id,`,
    `  position_x, position_y, position_z,`,
    `  camera_distance,`,
    `  camera_position_x, camera_position_y, camera_position_z,`,
    `  camera_focal_offset_x, camera_focal_offset_y, camera_focal_offset_z,`,
    `  camera_rotation_azmuth_angle, camera_rotation_polar_angle,`,
    `  created_by`,
    `) VALUES (`,
    `  ${lVar},`,
    `  ${n(cfg.position[0])}, ${n(cfg.position[1])}, ${n(cfg.position[2])},`,
    `  ${n(cfg.camera.distance)},`,
    `  ${n(cfg.camera.position[0])}, ${n(cfg.camera.position[1])}, ${n(cfg.camera.position[2])},`,
    `  ${n(cfg.camera.focalOffset[0])}, ${n(cfg.camera.focalOffset[1])}, ${n(cfg.camera.focalOffset[2])},`,
    `  ${n(cfg.camera.rotation.azimuthAngle)}, ${n(cfg.camera.rotation.polarAngle)},`,
    `  v_updated_by`,
    `);`,
  ].join('\n'));

  // Merge primaryCategoryId into the list in case the user set it via the dropdown
  // but didn't also tick it in the checkboxes — it still needs a row in label_categories.
  const catIdsForInsert = [...label.categoryIds];
  if (label.primaryCategoryId) {
    const alreadyPresent = catIdsForInsert.some(
      (c) => c.id === label.primaryCategoryId!.id && c.groupId === label.primaryCategoryId!.groupId,
    );
    if (!alreadyPresent) catIdsForInsert.push(label.primaryCategoryId);
  }
  if (catIdsForInsert.length > 0) {
    const rows = catIdsForInsert.map((c) => {
      const isPrimary = label.primaryCategoryId?.id === c.id && label.primaryCategoryId?.groupId === c.groupId;
      return `  (${lVar}, ${c.id}, ${b(isPrimary)}, v_updated_by)`;
    }).join(',\n');
    blocks.push(`INSERT INTO label_categories (label_id, category_id, "primary", created_by) VALUES\n${rows};`);
  }

  if (label.unitIds.length > 0) {
    const rows = label.unitIds.map((id) => `  (${lVar}, ${id}, v_updated_by)`).join(',\n');
    blocks.push(`INSERT INTO label_units (label_id, unit_id, created_by) VALUES\n${rows};`);
  }

  return blocks;
}

// ─── UPDATE diff builders (edit mode) ─────────────────────────────────────────

function diffSubject(draft: SubjectDraft, initial: SubjectDraft): string | null {
  const sets: string[] = [];
  if (draft.title !== initial.title)               sets.push(`  name = ${q(draft.title)}`);
  if (draft.slug !== initial.slug)                 sets.push(`  slug = ${q(draft.slug)}`);
  if (draft.description !== initial.description)   sets.push(`  description = ${q(draft.description || null)}`);
  if (draft.disciplineId !== initial.disciplineId) sets.push(`  discipline_id = ${n(draft.disciplineId)}`);
  if (draft.slug !== initial.slug) sets.push(`  thumbnail_path = ${q(deriveThumbnailPath(draft.slug))}`);
  if (draft.type !== initial.type)                 sets.push(`  type = ${q(draft.type)}`);
  if (sets.length === 0) return null;

  sets.push(`  updated_by = v_updated_by`);
  sets.push(`  updated_date = CURRENT_TIMESTAMP`);
  return [`UPDATE subjects SET`, sets.join(',\n'), `WHERE id = v_subject_id;`].join('\n');
}

function diffConfig3D(draft: SubjectDraft, initial: SubjectDraft): string | null {
  const sets: string[] = [];
  for (const field of CONFIG3D_FIELDS) {
    const newVal = field.get(draft.config3D);
    const oldVal = field.get(initial.config3D);
    if (String(newVal) !== String(oldVal)) {
      if (field.varName && newVal) {
        sets.push(`  ${field.col} = ${field.varName}`);
      } else {
        sets.push(`  ${field.col} = ${fmtVal(newVal, field.fmt)}`);
      }
    }
  }
  if (sets.length === 0) return null;

  sets.push(`  updated_by = v_updated_by`);
  sets.push(`  updated_date = CURRENT_TIMESTAMP`);
  return [`UPDATE subject_3d_configs SET`, sets.join(',\n'), `WHERE subject_id = v_subject_id;`].join('\n');
}

function diffSubjectCategories(draft: SubjectDraft, initial: SubjectDraft): string[] {
  const isSame = (a: SubjectDraft['categoryIds'][0], bb: SubjectDraft['categoryIds'][0]) =>
    a.id === bb.id && a.groupId === bb.groupId;
  const added   = draft.categoryIds.filter((c) => !initial.categoryIds.some((o) => isSame(c, o)));
  const removed = initial.categoryIds.filter((c) => !draft.categoryIds.some((o) => isSame(c, o)));
  const blocks: string[] = [];

  if (removed.length > 0) {
    blocks.push(
      `DELETE FROM subject_categories\nWHERE subject_id = v_subject_id AND category_id IN (${removed.map((c) => c.id).join(', ')});`
    );
  }
  if (added.length > 0) {
    const rows = added.map((c) => `  (v_subject_id, ${c.id}, v_updated_by)`).join(',\n');
    blocks.push(`INSERT INTO subject_categories (subject_id, category_id, created_by) VALUES\n${rows};`);
  }
  return blocks;
}

function diffSubjectUnits(draft: SubjectDraft, initial: SubjectDraft): string[] {
  const added   = draft.unitIds.filter((id) => !initial.unitIds.includes(id));
  const removed = initial.unitIds.filter((id) => !draft.unitIds.includes(id));
  const blocks: string[] = [];

  if (removed.length > 0) {
    blocks.push(`DELETE FROM subject_units\nWHERE subject_id = v_subject_id AND unit_id IN (${removed.join(', ')});`);
  }
  if (added.length > 0) {
    const rows = added.map((id) => `  (v_subject_id, ${id}, v_updated_by)`).join(',\n');
    blocks.push(`INSERT INTO subject_units (subject_id, unit_id, created_by) VALUES\n${rows};`);
  }
  return blocks;
}

interface LabelDiffResult {
  blocks: string[];
  /** Label slugs that need a variable declared (new or changed labels). */
  varSlugs: Set<string>;
}

function diffLabels(draft: SubjectDraft, initial: SubjectDraft): LabelDiffResult {
  const blocks: string[] = [];
  const varSlugs = new Set<string>();

  // Deleted labels — no variable needed; delete by slug + subject FK
  const deleted = initial.labels.filter((il) => !draft.labels.some((dl) => dl.slug === il.slug));
  for (const label of deleted) {
    blocks.push(
      `-- Delete label: ${label.title || label.slug}`,
      `DELETE FROM labels WHERE slug = ${q(label.slug)} AND subject_id = v_subject_id;` +
      `  -- cascades to label_3d_configs, label_categories, label_units`,
    );
  }

  // New labels — INSERT ... RETURNING id INTO variable
  const added = draft.labels.filter((dl) => !initial.labels.some((il) => il.slug === dl.slug));
  for (const label of added) {
    varSlugs.add(label.slug);
    blocks.push(`-- New label: ${label.title || label.slug}`, ...insertLabel(label, draft.type));
  }

  // Updated labels — SELECT id INTO variable first, then diff
  for (const draftLabel of draft.labels) {
    const initLabel = initial.labels.find((il) => il.slug === draftLabel.slug);
    if (!initLabel) continue;

    const lVar = labelVar(initLabel.slug);

    const labelSets: string[] = [];
    if (draftLabel.title !== initLabel.title)             labelSets.push(`  name = ${q(draftLabel.title)}`);
    if (draftLabel.slug !== initLabel.slug)               labelSets.push(`  slug = ${q(draftLabel.slug)}`);
    if (draftLabel.description !== initLabel.description) labelSets.push(`  description = ${q(draftLabel.description || null)}`);

    const cfg3DSets: string[] = [];
    const dc = draftLabel.config3D;
    const ic = initLabel.config3D;
    if (String(dc.position[0]) !== String(ic.position[0])) cfg3DSets.push(`  position_x = ${n(dc.position[0])}`);
    if (String(dc.position[1]) !== String(ic.position[1])) cfg3DSets.push(`  position_y = ${n(dc.position[1])}`);
    if (String(dc.position[2]) !== String(ic.position[2])) cfg3DSets.push(`  position_z = ${n(dc.position[2])}`);
    if (String(dc.camera.distance) !== String(ic.camera.distance))               cfg3DSets.push(`  camera_distance = ${n(dc.camera.distance)}`);
    if (String(dc.camera.position[0]) !== String(ic.camera.position[0]))         cfg3DSets.push(`  camera_position_x = ${n(dc.camera.position[0])}`);
    if (String(dc.camera.position[1]) !== String(ic.camera.position[1]))         cfg3DSets.push(`  camera_position_y = ${n(dc.camera.position[1])}`);
    if (String(dc.camera.position[2]) !== String(ic.camera.position[2]))         cfg3DSets.push(`  camera_position_z = ${n(dc.camera.position[2])}`);
    if (String(dc.camera.focalOffset[0]) !== String(ic.camera.focalOffset[0]))   cfg3DSets.push(`  camera_focal_offset_x = ${n(dc.camera.focalOffset[0])}`);
    if (String(dc.camera.focalOffset[1]) !== String(ic.camera.focalOffset[1]))   cfg3DSets.push(`  camera_focal_offset_y = ${n(dc.camera.focalOffset[1])}`);
    if (String(dc.camera.focalOffset[2]) !== String(ic.camera.focalOffset[2]))   cfg3DSets.push(`  camera_focal_offset_z = ${n(dc.camera.focalOffset[2])}`);
    if (String(dc.camera.rotation.azimuthAngle) !== String(ic.camera.rotation.azimuthAngle)) cfg3DSets.push(`  camera_rotation_azmuth_angle = ${n(dc.camera.rotation.azimuthAngle)}`);
    if (String(dc.camera.rotation.polarAngle)   !== String(ic.camera.rotation.polarAngle))   cfg3DSets.push(`  camera_rotation_polar_angle = ${n(dc.camera.rotation.polarAngle)}`);

    const isSame = (a: LabelDraft['categoryIds'][0], bb: LabelDraft['categoryIds'][0]) =>
      a.id === bb.id && a.groupId === bb.groupId;
    const addedCats   = draftLabel.categoryIds.filter((c) => !initLabel.categoryIds.some((o) => isSame(c, o)));
    const removedCats = initLabel.categoryIds.filter((c) => !draftLabel.categoryIds.some((o) => isSame(c, o)));
    const addedUnits   = draftLabel.unitIds.filter((id) => !initLabel.unitIds.includes(id));
    const removedUnits = initLabel.unitIds.filter((id) => !draftLabel.unitIds.includes(id));

    const primaryChanged =
      (draftLabel.primaryCategoryId?.id ?? null) !== (initLabel.primaryCategoryId?.id ?? null) ||
      (draftLabel.primaryCategoryId?.groupId ?? null) !== (initLabel.primaryCategoryId?.groupId ?? null);

    const hasChanges =
      labelSets.length > 0 || cfg3DSets.length > 0 ||
      addedCats.length > 0 || removedCats.length > 0 ||
      addedUnits.length > 0 || removedUnits.length > 0 ||
      primaryChanged;
    if (!hasChanges) continue;

    varSlugs.add(initLabel.slug);
    const labelBlocks: string[] = [];

    labelBlocks.push(`SELECT id INTO ${lVar} FROM labels WHERE slug = ${q(initLabel.slug)} AND subject_id = v_subject_id;`);

    if (labelSets.length > 0) {
      labelSets.push(`  updated_by = v_updated_by`, `  updated_date = CURRENT_TIMESTAMP`);
      labelBlocks.push(`UPDATE labels SET\n${labelSets.join(',\n')}\nWHERE id = ${lVar};`);
    }
    if (cfg3DSets.length > 0) {
      cfg3DSets.push(`  updated_by = v_updated_by`, `  updated_date = CURRENT_TIMESTAMP`);
      labelBlocks.push(`UPDATE label_3d_configs SET\n${cfg3DSets.join(',\n')}\nWHERE label_id = ${lVar};`);
    }
    if (removedCats.length > 0) {
      labelBlocks.push(`DELETE FROM label_categories WHERE label_id = ${lVar} AND category_id IN (${removedCats.map((c) => c.id).join(', ')});`);
    }
    if (addedCats.length > 0) {
      const rows = addedCats.map((c) => {
        const isPrimary = draftLabel.primaryCategoryId?.id === c.id && draftLabel.primaryCategoryId?.groupId === c.groupId;
        return `  (${lVar}, ${c.id}, ${b(isPrimary)}, v_updated_by)`;
      }).join(',\n');
      labelBlocks.push(`INSERT INTO label_categories (label_id, category_id, "primary", created_by) VALUES\n${rows};`);
    }
    if (primaryChanged) {
      // Reset all categories for this label to non-primary, then set the new primary.
      labelBlocks.push(`UPDATE label_categories SET "primary" = false, updated_by = v_updated_by, updated_date = CURRENT_TIMESTAMP WHERE label_id = ${lVar};`);
      if (draftLabel.primaryCategoryId) {
        labelBlocks.push(`UPDATE label_categories SET "primary" = true, updated_by = v_updated_by, updated_date = CURRENT_TIMESTAMP WHERE label_id = ${lVar} AND category_id = ${draftLabel.primaryCategoryId.id};`);
      }
    }
    if (removedUnits.length > 0) {
      labelBlocks.push(`DELETE FROM label_units WHERE label_id = ${lVar} AND unit_id IN (${removedUnits.join(', ')});`);
    }
    if (addedUnits.length > 0) {
      const rows = addedUnits.map((id) => `  (${lVar}, ${id}, v_updated_by)`).join(',\n');
      labelBlocks.push(`INSERT INTO label_units (label_id, unit_id, created_by) VALUES\n${rows};`);
    }

    blocks.push(`-- Updated label: ${draftLabel.title || draftLabel.slug}`, ...labelBlocks);
  }

  return { blocks, varSlugs };
}

// ─── Asset variable + warning builders ────────────────────────────────────────

const ASSET_FIELDS: Array<{ field: string; get: (c: Config3D) => string }> = [
  { field: 'Subject FBX',          get: (c) => c.modelPath },
  { field: 'Texture',               get: (c) => c.texturePath },
  { field: 'Context Model FBX',     get: (c) => c.contextModelPath },
  { field: 'Reference Context FBX', get: (c) => c.referenceContextModelPath },
];

/**
 * Returns DECLARE lines for asset path variables that are being set.
 * New mode: all non-empty asset fields. Edit mode: only changed ones.
 */
function buildAssetVarDecls(draft: SubjectDraft, isNew: boolean, initial: SubjectDraft): string[] {
  const decls = CONFIG3D_FIELDS
    .filter((f) => !!f.varName)
    .flatMap((f) => {
      const newVal = f.get(draft.config3D);
      if (!newVal) return [];
      if (!isNew && String(newVal) === String(f.get(initial.config3D))) return [];
      const path = ASSET_PATH_PREFIX + baseName(String(newVal));
      return [`${f.varName}  VARCHAR(500) := '${path}';`];
    });

  if (isNew) {
    decls.push(`v_thumbnail_path  VARCHAR(500) := '${deriveThumbnailPath(draft.slug)}';`);
  }

  return decls;
}

function buildAssetWarnings(draft: SubjectDraft, initial: SubjectDraft, isNew: boolean): AssetWarning[] {
  const warnings = ASSET_FIELDS.flatMap(({ field, get }) => {
    const newPath = get(draft.config3D);
    if (!newPath) return [];
    if (isNew) return [{ field, path: newPath, isAdded: true }];
    const oldPath = get(initial.config3D);
    if (newPath !== oldPath) return [{ field, path: newPath, isAdded: !oldPath }];
    return [];
  });

  const newThumbPath = deriveThumbnailPath(draft.slug);
  if (isNew) {
    warnings.push({ field: 'Thumbnail', path: newThumbPath, isAdded: true });
  } else if (newThumbPath !== deriveThumbnailPath(initial.slug)) {
    warnings.push({ field: 'Thumbnail', path: newThumbPath, isAdded: false });
  }

  return warnings;
}

// ─── DO block assembly ────────────────────────────────────────────────────────

/** Indent every non-empty line of a (possibly multi-line) block by `spaces` spaces. */
const indentBlock = (block: string, spaces = 2): string =>
  block.split('\n').map((line) => (line.trim() ? ' '.repeat(spaces) + line : '')).join('\n');

function buildDoBlock(declares: string[], bodyBlocks: string[]): string {
  const declareSection = declares.map((d) => `  ${d}`).join('\n');
  const bodySection    = bodyBlocks.map((b) => indentBlock(b, 2)).join('\n\n');
  return ['DO $$', 'DECLARE', declareSection, 'BEGIN', bodySection, 'END $$;'].join('\n');
}

// ─── Public entry point ────────────────────────────────────────────────────────

export function buildSqlStatements(
  draft: SubjectDraft,
  areaSlug: string,
  isNew: boolean,
  initialDraft: SubjectDraft,
): SqlOutput {
  const header = [
    `-- ================================================================`,
    isNew
      ? `-- New ${draft.type} subject: ${draft.title || '(untitled)'}  [${draft.slug || '(no-slug)'}]`
      : `-- Edit ${draft.type} subject: ${initialDraft.title}  [${initialDraft.slug}]`,
    `-- Area: ${areaSlug}`,
    `-- Set v_updated_by in the DECLARE section to the actual username.`,
    `-- ================================================================`,
  ].join('\n');

  if (isNew) {
    const declares = [
      `v_updated_by  VARCHAR(255) := 'admin';  -- replace with actual username`,
      ...buildAssetVarDecls(draft, true, initialDraft),
      `v_area_id    INTEGER;`,
      `v_subject_id INTEGER;`,
      ...draft.labels.map((l) => `${labelVar(l.slug)} INTEGER;`),
    ];

    const body: string[] = [
      `-- Resolve area\nSELECT id INTO v_area_id FROM areas WHERE slug = ${q(areaSlug)};`,
      `-- subjects\n${insertSubject(draft)}`,
    ];

    if (draft.type === '3D') {
      body.push(`-- subject_3d_configs\n${insertConfig3D(draft)}`);
    }

    const catSql = insertSubjectCategories(draft);
    if (catSql) body.push(`-- subject_categories (${draft.categoryIds.length})\n${catSql}`);

    const unitSql = insertSubjectUnits(draft);
    if (unitSql) body.push(`-- subject_units (${draft.unitIds.length})\n${unitSql}`);

    for (const label of draft.labels) {
      body.push(
        `-- Label: ${label.title || label.slug}`,
        ...insertLabel(label, draft.type),
      );
    }

    return {
      sql: [header, '', 'BEGIN;', '', buildDoBlock(declares, body), '', 'COMMIT;'].join('\n'),
      assetWarnings: buildAssetWarnings(draft, initialDraft, isNew),
    };
  }

  // ── Edit mode ────────────────────────────────────────────────────────────────

  // Compute diffs first so we know which label variables are needed
  const subjectSql   = diffSubject(draft, initialDraft);
  const config3DSql  = draft.type === '3D' ? diffConfig3D(draft, initialDraft) : null;
  const catBlocks    = diffSubjectCategories(draft, initialDraft);
  const unitBlocks   = diffSubjectUnits(draft, initialDraft);
  const { blocks: labelBlocks, varSlugs } = diffLabels(draft, initialDraft);

  const hasChanges =
    subjectSql || config3DSql || catBlocks.length > 0 || unitBlocks.length > 0 || labelBlocks.length > 0;

  if (!hasChanges) {
    return {
      sql: [header, '', '-- No changes detected.'].join('\n'),
      assetWarnings: [],
    };
  }

  const declares = [
    `v_updated_by  VARCHAR(255) := 'admin';  -- replace with actual username`,
    ...buildAssetVarDecls(draft, false, initialDraft),
    `v_area_id    INTEGER;`,
    `v_subject_id INTEGER;`,
    ...[...varSlugs].map((slug) => `${labelVar(slug)} INTEGER;`),
  ];

  const body: string[] = [
    `-- Resolve area and subject\n` +
    `SELECT id INTO v_area_id FROM areas WHERE slug = ${q(areaSlug)};\n` +
    `SELECT id INTO v_subject_id FROM subjects WHERE slug = ${q(initialDraft.slug)} AND area_id = v_area_id;`,
  ];

  if (subjectSql)  body.push(`-- subjects\n${subjectSql}`);
  if (config3DSql) body.push(`-- subject_3d_configs\n${config3DSql}`);
  if (catBlocks.length  > 0) body.push(`-- subject_categories`, ...catBlocks);
  if (unitBlocks.length > 0) body.push(`-- subject_units`, ...unitBlocks);
  if (labelBlocks.length > 0) body.push(...labelBlocks);

  return {
    sql: [header, '', 'BEGIN;', '', buildDoBlock(declares, body), '', 'COMMIT;'].join('\n'),
    assetWarnings: buildAssetWarnings(draft, initialDraft, isNew),
  };
}
